import React, { useContext, useMemo, useState } from "react";
import { useTextDomainContext, useUser } from "@/app//providers";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { Button, Stack } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import type { TypedDocumentNode } from "@apollo/client";
import type { Query, QueryTenantsListArgs } from "@/types";
import {
  ActionMenu,
  BackButton,
  ConfirmNotification,
  TableView1,
} from "@/components";
import { string, bool } from "prop-types";
import { useQuery } from "@apollo/client/react";
import {
  // CUSTOMERS,
  // TENANTS_COMPANIES_LIST,
  // TENANTS_LIST,
  usePageTitle,
  useRemoveCompanyFromTenant,
  useDeleteCompany,
  useHasPermission,
} from "@/hooks";
import { LinkCompany } from "@/pages";
import { queries } from "@/graphql";
import {
  CompanyAction,
  CompanyListProps,
  CompanyRow,
  ConfirmResult,
} from "./types";
import {
  permissionsCompaniesReadonly,
  permissionsCompaniesFull,
} from "@/utils";

// import DeleteModel from "./components/DeleteModel";
// import LoginModel from "./components/LoginModel";
// import UserStatusFilter from "./UserStatusFilter";
// import EditModal from "./components/EditModal";

const feature = "common";

export const CompanyList = (props: CompanyListProps) => {
  //#region Permissions
  const { allow: allowCompaniesReadonly } = useHasPermission(
    permissionsCompaniesReadonly,
    feature,
  );
  const { allow: allowCompaniesFull } = useHasPermission(
    permissionsCompaniesFull,
    feature,
  );
  //#endregion Permissions

  //#region Loading data
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { tenantId: parmsTenantId = "" } = useParams();
  const parentPath = props.parentPath || "system-admin";
  const tenantId = parmsTenantId || user?.currentTenantId || "";
  const parmCompanyFilter = props.companyFilter || "";
  const parmParentChildfilter = props.parentChildfilter || "";

  // Set page title
  usePageTitle(
    parmParentChildfilter === "child"
      ? gettext("Child Companies")
      : gettext("Parent Companies"),
  );

  const showItems = useMemo(() => {
    return {
      showAdd: (props.showAdd && allowCompaniesFull) ?? false,
      showAddChildCompany: (props.showAdd && allowCompaniesFull) ?? false,
      showEdit: (props.showEdit && allowCompaniesFull) ?? false,
      showDelete:
        (props.showDelete && allowCompaniesFull) || allowCompaniesFull,
      showUnlink: false,
      showChildCompanies: allowCompaniesReadonly,
    };
  }, [
    props.showAdd,
    props.showDelete,
    props.showEdit,
    allowCompaniesFull,
    allowCompaniesReadonly,
  ]);

  // const { customers, loading } = useCompanies();
  const allCustomersVariables = useMemo(() => {
    let queryPartnerId = tenantId;

    if (!queryPartnerId) {
      if (
        parentPath.includes("partner-admin") ||
        parentPath.includes("corporate-group")
      ) {
        queryPartnerId = "none";
      }
    }

    const variables: Record<string, unknown> = {};

    if (parmCompanyFilter === "all") {
      if (queryPartnerId && parmParentChildfilter === "child") {
        variables.tenantId = queryPartnerId;
      }

      if (parmParentChildfilter === "child") {
        variables.customerType = "child";
      } else {
        variables.customerType = "parent";
      }
    } else if (queryPartnerId) {
      variables.tenantId = queryPartnerId;
    }

    return variables;
  }, [tenantId, parentPath, parmCompanyFilter, parmParentChildfilter]);

  const { data: allCustomers, loading: loadingAll } = useQuery(
    queries.customers,
    {
      variables: allCustomersVariables,
    },
  );
  // useLazyQuery(CUSTOMERS);

  // const [
  //   getTenantCustomers,
  //   { data: tenantCustomers, loading: loadingTenant },
  // ] = useLazyQuery(TENANTS_COMPANIES_LIST);

  const tenantsListQuery = queries.tenantsList as TypedDocumentNode<
    Pick<Query, "tenantsList">,
    QueryTenantsListArgs
  >;

  const { data: tenantListData } = useQuery(tenantsListQuery, {
    variables: { tenantId },
    skip: !tenantId,
  });

  const { removeCompanyFromTenant } = useRemoveCompanyFromTenant();
  const { deleteCompany } = useDeleteCompany();

  //#endregion Loading data

  //#region useState
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editRow, setEditRow] = useState({ tenantId });
  const [showConfirm, setShowConfirm] = useState(false);
  const [dialogParameters, setDialogParameters] = useState<{
    action?: CompanyAction;
    id?: string | number;
  }>({});
  const [confirmMessage, setConfirmMessage] = useState("");
  const tenantName = tenantListData?.tenantsList?.[0]?.name || "";

  // const [changePasswordFormOpen, setChangePasswordFormOpen] = useState(false);
  // const [loading, setLoading] = useState(false);
  // const [statusFilter, setStatusFilter] = useState("Active");
  // const [editableRow, seteditableRow] = useState({
  // });
  // const [delete_show, setdelete] = useState(false);
  // const [toastList, updateToastList] = useState([]);
  // const [edit_show, setedit] = useState(false);
  // const [chpass_show, setchpass] = useState(false);
  // const [login_show, setlogin] = useState(false);

  //#endregion useState

  // Compute derived values
  const backLink = tenantId
    ? location.state?.parentPath || `/${parentPath}/tenants`
    : "";
  // const title =
  //   parmCompanyFilter === "all"
  //     ? gettext("Companies")
  //     : gettext("Companies for the corporate group: ") + tenantName;

  const title = useMemo(() => {
    if (parmCompanyFilter === "all" || !tenantId) {
      if (parmParentChildfilter === "child") {
        return gettext("Child Companies");
      }
      return gettext("Parent Companies");
    } else {
      return gettext("Companies for the corporate group: ") + tenantName;
    }
  }, [parmCompanyFilter, tenantId, parmParentChildfilter, tenantName, gettext]);

  const rawCustomers = (allCustomers as any)?.getCustomers || [];
  const customers = useMemo(() => {
    if (parmParentChildfilter === "child") {
      return rawCustomers.filter((c: any) => c.customerType === "child");
    }
    return rawCustomers.filter(
      (c: any) => c.customerType === "parent" || c.customerType !== "child",
    );
  }, [rawCustomers, parmParentChildfilter]);

  // const customers =
  //   parmCompanyFilter === "all"
  //     ? (allCustomers as any)?.getCustomers || []
  //     : (tenantCustomers as any)?.tenantsCompaniesList || [];

  // const { searchString } = props;
  // const handleClose_delete = () => setdelete(false);
  // const handleShow_delete = () => setdelete(true);
  // const handleOpen = () => setAddModalOpen(true);
  // const handleClose = () => setAddModalOpen(false);
  // const handleClose_edit = () => setedit(false);
  // const handleShow_edit = () => setedit(true);
  // const handleClose_login = () => setlogin(false);
  // const { usersList, loading } = useUsersList(searchString, statusFilter);
  // const handleClose_chpass = message => {
  //   if (message === "success") {
  //     updateToastList([
  //       {
  //         title: "Success",
  //         description: "Password updated successfully",
  //         type: "success"
  //       }
  //     ]);
  //   }
  //   setchpass(false);
  // };
  // const handleShow_chpass = () => setchpass(true);
  // const handleShow_login = () => setlogin(true);

  /**
   *
   * @param row
   */
  // function edit_functionality(row) {
  //   seteditableRow(row);
  //
  //   handleShow_edit();
  // }

  /**
   *
   * @param row
   */
  // function chpass_functionality(row) {
  //   seteditableRow(row);
  //
  //   handleShow_chpass();
  // }

  /**
   *
   * @param row
   */
  // function login_functionality() {
  //   // row
  //   // seteditableRow(row);
  //   // handleShow_login();
  // }

  /**
   *
   * @param row
   */
  // function delete_functionality() {
  //   // row
  //   // seteditableRow(row);
  //   // handleShow_delete();
  // }

  /**
   *
   * @param row
   */
  // function roles_functionality(row) {
  //   navigate(`/system-admin/users/${row.id}/update-role`);
  // }

  /**
   *
   * @param row
   */
  // function customers_functionality(row) {
  //   navigate(`${location.pathname}/${row.id}/customers`, {
  //     state: {
  //       user: row,
  //     },
  //   });
  // }

  /**
   *
   * @param row
   * @param action
   */

  /**
   *
   * @param row
   * @param action
   */
  // function performAction(row, action) {
  //   switch (action) {
  //     case "edit":
  //       handleEdit(row);
  //       break;
  //     case "delete":
  //       delete_functionality(row);
  //       break;
  //     case "roles":
  //       roles_functionality(row);
  //       break;
  //     case "customers":
  //       customers_functionality(row);
  //       break;
  //     case "change-password":
  //       // handleChangePassword(row);
  //       // chpass_functionality(row);
  //       break;
  //     case "login":
  //       login_functionality(row);
  //       break;
  //     default:
  //       break;
  //   }
  // }

  /**
   *
   * @param params
   */
  // function columnFormatter(params) {
  //   return (
  //     <div className="w-100 d-flex justify-content-center">
  //       {!!props.showEdit && (
  //         <Tooltip title={gettext("Edit")} arrow>
  //           <IconButton onClick={() => performAction(params.row, "edit")}>
  //             <FaUserEdit size={16} />
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //
  //       {!!props.showDeleteSingle && (
  //         <Tooltip title={gettext("Delete")} arrow>
  //           <IconButton onClick={() => performAction(params.row, "delete")}>
  //             <MdDelete size={16} />
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //
  //       {/*{!!props.showChangePassword && (*/}
  //       {/*  <Tooltip title={gettext("Change Password")} arrow>*/}
  //       {/*    <IconButton onClick={() => performAction(params.row, "change-password")}>*/}
  //       {/*      <MdPassword size={16}/>*/}
  //       {/*    </IconButton>*/}
  //       {/*  </Tooltip>*/}
  //       {/*)}*/}
  //
  //       {/* {!restricted && ( */}
  //       {/*   <Tooltip title={gettext("Update Roles")} arrow> */}
  //       {/*     <IconButton onClick={() => performAction(params.row, "roles")}> */}
  //       {/*       <IoKeySharp size={16} /> */}
  //       {/*     </IconButton> */}
  //       {/*   </Tooltip> */}
  //       {/* )} */}
  //
  //       {/*{!!props.showCompanies && (*/}
  //       {/*  <Tooltip title={gettext("Companies")} arrow>*/}
  //       {/*    <IconButton onClick={() => performAction(params.row, "customers")}>*/}
  //       {/*      <HiMiniBuildingOffice2 size={16}/>*/}
  //       {/*      /!*<IoPersonSharp size={16} />*!/*/}
  //       {/*    </IconButton>*/}
  //       {/*  </Tooltip>*/}
  //       {/*)}*/}
  //
  //       {/*{!!props.showLoginAs && (*/}
  //       {/*  <Tooltip title={gettext("Login as")} arrow>*/}
  //       {/*    <IconButton onClick={() => performAction(params.row, "login")}>*/}
  //       {/*      <MdLogin size={16}/>*/}
  //       {/*    </IconButton>*/}
  //       {/*  </Tooltip>*/}
  //       {/*)}*/}
  //     </div>
  //   );
  // }

  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
      // flex: 1, // Adjust this flex value as needed
    },
    {
      field: "name",
      headerName: gettext("Name"),
      headerAlign: "left",
      type: "string",
      flex: 1,
    },
    {
      field: "cvr",
      headerName: gettext("Company Number"),
      headerAlign: "left",
      type: "string",
      minWidth: 150,
      // flex: 1,
    },
    {
      field: "customerType",
      headerName: gettext("Parent / Child"),
      headerAlign: "left",
      type: "string",
      minWidth: 150,
      // flex: 1,
    },
    {
      field: "city",
      headerName: gettext("City"),
      headerAlign: "left",
      type: "string",
      minWidth: 150,
      // flex: 1,
    },
    {
      field: "country",
      headerName: gettext("Country"),
      headerAlign: "left",
      type: "string",
      minWidth: 75,
      // flex: 1
    },
    // {
    //   field: "roles",
    //   headerName: gettext("Role"),
    //   headerAlign: "left",
    //   type: "string",
    //   flex: 1
    // },
    // {
    //   field: "countryCode",
    //   headerName: gettext("Country"),
    //   headerAlign: "left",
    //   type: "string",
    //   flex: 1
    // },
    // {
    //   field: "timezone",
    //   headerName: gettext("Time Zone"),
    //   headerAlign: "left",
    //   type: "string",
    //   flex: 1
    // },
    // {
    //   field: "status",
    //   headerName: gettext("Status"),
    //   headerAlign: "left",
    //   type: "string",
    //   flex: 1
    // },
    // {
    //   field: "partnerName",
    //   headerName: gettext("Partner"),
    //   headerAlign: "left",
    //   type: "string",
    //   flex: 1
    // },
    {
      field: "actions",
      type: "actions",
      headerName: gettext("Actions"),
      hideable: false,
      maxWidth: 75,
      align: "right",
      headerAlign: "right",
      // renderCell: (params) => columnFormatter(params),
      renderCell: (params: GridRenderCellParams<CompanyRow>) => (
        <ActionMenu
          menuFor="company"
          row={params.row}
          menuClick={menuClick}
          showItems={showItems}
        />
      ),
    },
  ];

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        // name: true,
        // email: true,
        // role: false,
        // country: false,
        // timezone: false,
        // status: true,
        // partnerName: false,
      },
    },
  };

  // const handleGetSelectedValues = (selectedValues) => {
  //
  // };

  const handleOnClickRemoveLink = (
    action: CompanyAction,
    id?: string | number,
  ) => {
    setDialogParameters({
      action,
      id,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: ConfirmResult) => {
    if (obj.result === "yes") {
      switch (obj?.parameters?.action) {
        case "company-delete":
          deleteCompany({
            companyId: String(obj?.parameters?.id),
          });
          break;
        case "company-unlink":
          if (tenantId) {
            removeCompanyFromTenant({
              tenantId,
              companyId: String(obj?.parameters?.id),
            });
          }
          break;
        default:
          break;
      }
    }
    setShowConfirm(false);
  };

  const preToolbar = () => {
    return (
      <>
        {/*{!tenantId && (*/}
        {/*  <Button*/}
        {/*    variant="contained"*/}
        {/*    component={Link}*/}
        {/*    to={"/developer/companies/add"}*/}
        {/*  >*/}
        {/*    {gettext("Add company")}*/}
        {/*  </Button>*/}
        {/*)}*/}
        {/*{tenantId && (*/}
        {/*  <Button*/}
        {/*    variant="contained"*/}
        {/*    onClick={() => {*/}
        {/*      setAddFormOpen(true);*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    {gettext("Link company")}*/}
        {/*  </Button>*/}
        {/*)}*/}
        {/*<Button*/}
        {/*  disabled={disabledDeleteButton}*/}
        {/*  variant="contained"*/}
        {/*  color={"error"}*/}
        {/*>*/}
        {/*  Delete*/}
        {/*</Button>*/}
      </>
    );
  };

  const postToolbar = () => {
    const showPostToolbar = backLink || props.showAdd || tenantId;
    return (
      <>
        {showPostToolbar && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ pl: 1, alignItems: "center" }}
          >
            {backLink && <BackButton link={`${backLink}`} />}

            {/*{props.showAdd && (*/}
            {/*  <Button*/}
            {/*    variant="contained"*/}
            {/*    onClick={() => {*/}
            {/*      setAddFormOpen(true);*/}
            {/*    }}*/}
            {/*    sx={{*/}
            {/*      ml: 1,*/}
            {/*    }}*/}
            {/*  >*/}
            {/*    {gettext("Add User")}*/}
            {/*  </Button>*/}
            {/*)}*/}

            {!parmsTenantId && showItems.showAdd && (
              <Button
                variant="contained"
                component={Link}
                to={`/${parentPath}/companies/add`}
                state={{
                  parentPath: location.pathname,
                  customerType: "parent",
                }}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {gettext("Add parent company")}
              </Button>
            )}
            {tenantId && (
              <Button
                variant="contained"
                onClick={() => {
                  setAddFormOpen(true);
                }}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {gettext("Link company")}
              </Button>
            )}
          </Stack>
        )}
      </>
    );
  };

  const menuClick = (action: CompanyAction, row: CompanyRow) => {
    switch (action) {
      case "company-child-companies":
        navigate(
          parmsTenantId
            ? `/${parentPath}/tenants/${tenantId}/companies/${row?.id}/child-companies`
            : `/${parentPath}/companies/${row?.id}/child-companies`,
          {
            state: {
              parentPath: location.pathname,
            },
          },
        );
        break;
      case "company-add-child":
        navigate(
          parmsTenantId
            ? `/${parentPath}/tenants/${tenantId}/companies/${row?.id}/child-companies/add`
            : `/${parentPath}/companies/${row?.id}/child-companies/add`,
          {
            state: {
              parentPath: location.pathname,
              parentCompany: row,
              parentCustomerId: row?.id,
              parentCvr: (row as any)?.cvr,
              parentName: (row as any)?.name,
              customerType: "child",
            },
          },
        );
        break;
      case "company-edit":
        navigate(
          parmsTenantId
            ? `/${parentPath}/tenants/${tenantId}/companies/edit/${row?.id}`
            : `/${parentPath}/companies/edit/${row?.id}`,
          {
            state: {
              parentPath: location.pathname,
              company: row,
            },
          },
        );
        break;
      case "company-delete":
        setConfirmMessage(gettext("Do you want to delete the company?"));
        handleOnClickRemoveLink(action, row?.id);
        break;
      case "company-unlink":
        setConfirmMessage(gettext("Do you want to unlink the company?"));
        handleOnClickRemoveLink(action, row?.id);
        break;
      default:
        break;
    }
  };

  const handleCloseAddForm = () => {
    setAddFormOpen(false);
    setEditRow({ tenantId });
  };

  return (
    <>
      <TableView1
        title={title}
        rows={customers}
        // rows={[]}
        columns={columns}
        loading={loadingAll}
        // loading={loadingAll || loadingTenant}
        initialState={initialState}
        preToolbar={preToolbar()}
        postToolbar={postToolbar()}
        showGridToolbar
        showGridToolbarExport={!!props.showExport}
        showGridToolbarQuickFilter
        beforeTable={
          <>
            {/* <UserStatusFilter */}
            {/*   setStatusFilter={setStatusFilter} */}
            {/*   gettext={gettext} */}
            {/* /> */}
            <br />
          </>
        }
      />
      <LinkCompany
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        title={gettext("Delete Company")}
        parameters={dialogParameters}
        getResult={handleGetResult}
        message={confirmMessage}
      />

      {/*<AddUser open={addFormOpen} handleClose={handleCloseAddForm} row={editRow}/>*/}
      {/*<ChangePassword open={changePasswordFormOpen} handleClose={handleCloseChangePasswordForm} row={editRow}/>*/}
      {/* <EditModal */}
      {/*   show={edit_show} */}
      {/*   onHide={handleClose_edit} */}
      {/*   editableRow={editableRow} */}
      {/* /> */}
      {/* <EditModel
                show={edit_show}
                onHide={handleClose_edit}
                editableRow={editableRow}
            /> */}
      {/*<ChPassModel*/}
      {/*  show={chpass_show}*/}
      {/*  onHide={handleClose_chpass}*/}
      {/*  editableRow={editableRow}*/}
      {/*/>*/}
      {/* <DeleteModel */}
      {/*   show={delete_show} */}
      {/*   onHide={handleClose_delete} */}
      {/*   editableRow={editableRow} */}
      {/*   getTeamMembers={props.getTeamMembers} */}
      {/* /> */}
      {/* <LoginModel */}
      {/*   show={login_show} */}
      {/*   onHide={handleClose_login} */}
      {/*   editableRow={editableRow} */}
      {/* /> */}
      {/*<Toast toastList={toastList}/>*/}
    </>
  );
};

CompanyList.propTypes = {
  parentPath: string,
  showAdd: bool,
  showExport: bool,
  showEdit: bool,
  showDelete: bool,
  showUnlink: bool,
  filter: string,
  // showChangePassword: bool,
  // showCompanies: bool,
  // showLoginAs: bool,
  // userFilter: oneOf(["all", "system", "partner", "company"]).isRequired
};
